import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import localApi from "../localApi";
import { toast } from "sonner";
import {
  MapPin, Clock, CalendarDays, Plane,
  Hotel, Tent, Car, Caravan, Ticket, Phone, Mail, Hash,
  GripVertical, Trash2, RotateCcw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ============ DATA ============

const accommodations = [
  { type: "hotel", name: "Bellagio Las Vegas", address: "3600 Las Vegas Blvd South, Las Vegas, NV 89109", dates: "17.-20. Juli 2026", nights: "3 Nachte", conf: "77L0ZCMSRS", phone: "888.987.6667", detail: "Fountain View Two Queen, MGM Rewards" },
  { type: "hotel", name: "Marina Escape 2BR Townhouse", address: "13924 Marquesas Way, Los Angeles, CA 90292", dates: "20.-23. Juli 2026", nights: "3 Nachte", conf: "6920124976", phone: "+1 818-388-2847", detail: "2-Schlafzimmer Apartment, Kostenlos Parken" },
  { type: "hotel", name: "Wuksachi Lodge", address: "64740 Wuksachi Way, Sequoia NP, CA 93262", dates: "23.-25. Juli 2026", nights: "2 Nachte", conf: "404SF358498", phone: "866-762-1325", detail: "Superior Room Two Queen Beds" },
  { type: "hotel", name: "The Ahwahnee", address: "1 Ahwahnee Dr., Yosemite NP, CA 95389", dates: "25.-27. Juli 2026", nights: "2 Nachte", conf: "100058968 / 16K3CM", detail: "Prime View Double" },
  { type: "hotel", name: "Argonaut Hotel", address: "495 Jefferson St, Fisherman's Wharf, San Francisco, CA 94109", dates: "27.-29. Juli 2026", nights: "2 Nachte", conf: "5018972355", phone: "+1 415-563-0800", detail: "Room with 2 Queen Beds" },
  { type: "camp", name: "Benbow KOA Holiday", address: "7000 Benbow Drive, Garberville, CA 95542", dates: "29.-30. Juli 2026", nights: "1 Nacht", conf: "21208357", phone: "1-707-923-2777", email: "koabenbow@gmail.com", detail: "Premium Pull Thru, 55', Full Hookups" },
  { type: "camp", name: "Crescent City/Redwoods KOA Holiday", address: "4241 Highway 101 North, Crescent City, CA 95531", dates: "30. Jul-1. Aug 2026", nights: "2 Nachte", phone: "1-707-464-5744", email: "info@crescentcitykoa.com", detail: "RV Sites" },
  { type: "camp", name: "Waldport/Newport KOA Journey", address: "1330 NW Pacific Coast Hwy, Waldport, OR 97394", dates: "2.-3. Aug 2026", nights: "1 Nacht", phone: "1-800-562-3443", email: "guestservices@waldportkoa.com", detail: "All Sites" },
  { type: "camp", name: "Bullards Beach State Park", address: "Bullards Beach State Park, Bandon, OR 97411", dates: "1.-2. Aug 2026", nights: "1 Nacht", conf: "2-38248479", detail: "Standard Campsite A02" },
  { type: "camp", name: "Astoria/Warrenton/Seaside KOA Resort", address: "1100 NW Ridge Road, Hammond, OR 97121", dates: "3.-4. Aug 2026", nights: "1 Nacht", phone: "1-503-861-2606", email: "astoriakoa@racpack.com", detail: "RV Sites" },
  { type: "hotel", name: "Quileute Oceanside Resort", address: "330 Ocean Front Drive, La Push, WA 98350", dates: "4.-5. Aug 2026", nights: "1 Nacht", conf: "2026018118", detail: "Oceanside Room" },
  { type: "camp", name: "Crescent Beach & RV Park", address: "2860 Crescent Beach Road, Port Angeles, WA 98363", dates: "5.-7. Aug 2026", nights: "2 Nachte", conf: "269599", phone: "360-928-3344", detail: "RV/Wohnmobil-Stellplatz" },
  { type: "hotel", name: "The Sound Hotel Belltown", address: "2120 4th Avenue, Seattle, WA 98121", dates: "7.-8. Aug 2026", nights: "1 Nacht", conf: "6917243789", phone: "+1 206-441-7456", detail: "Doppelzimmer" },
  { type: "hotel", name: "Berg Haus Leavenworth", address: "18800 Beaver Valley Rd, Leavenworth, WA 98826", dates: "8.-10. Aug 2026", nights: "2 Nachte", phone: "509-763-0180", detail: "Ferienhaus" },
  { type: "hotel", name: "Hotel 1000, Hyatt Unbound", address: "1000 1st Avenue, Seattle, WA 98104", dates: "10.-11. Aug 2026", nights: "1 Nacht", conf: "6369914287", phone: "+1 206-957-1000", detail: "Doppelzimmer" },
];

const activities = [
  { name: "The Sphere – Wizard of Oz", date: "18. Juli 2026, 11:00", location: "The Sphere, Las Vegas, NV", detail: "Doors open 10:15, Show 11:00 Uhr. Die grosste Kugel der Welt mit dem grossten LED-Bildschirm." },
  { name: "Grand Canyon Helikopter-Tour", date: "19. Juli 2026, 07:00", location: "Las Vegas, NV", detail: "Abflug 07:00 Uhr morgens. Flug uber den Grand Canyon." },
  { name: "Universal Studios Hollywood VIP Tour", date: "21. Juli 2026, 09:30", location: "1000 Universal Studios Blvd, North Hollywood, CA 91602", conf: "GYGVN3HA5MNH", detail: "5 Personen, Treffpunkt: VIP-Empfang rechts vom Haupteingang, Ankunft 09:10, Gratis Parken: Frankenstein Valet Garage", phone: "+1 800 864 8377" },
  { name: "Alcatraz Island Tour", date: "28. Juli 2026, 10:35", location: "Pier 33, San Francisco, CA", detail: "Fahre ab Pier 33 um 10:35 Uhr. Seid um 10:05 Uhr da! Audio-Guide inklusive." },
];

const flights = [
  { direction: "Hinflug", date: "17. Juli 2026", legs: [
    { from: "BER", to: "AMS", depTime: "12:10", arrTime: "13:30", airline: "KLM KL1776", aircraft: "Airbus A321NEO", duration: "1h20" },
    { from: "AMS", to: "LAS", depTime: "16:55", arrTime: "18:35", airline: "KLM KL635", aircraft: "Boeing 777-200", duration: "10h40", transfer: "3h25" },
  ]},
  { direction: "Rückflug", date: "11. August 2026", legs: [
    { from: "SEA", to: "CDG", depTime: "13:30", arrTime: "08:10 +1", airline: "Air France AF337", aircraft: "Airbus A350-900", duration: "9h40", terminal: "Terminal 2E" },
    { from: "CDG", to: "BER", depTime: "10:05", arrTime: "11:55", airline: "Air France AF1734", aircraft: "Airbus A220-300", duration: "1h50", transfer: "1h55", terminal: "Terminal 2F → Terminal 1" },
  ]},
];

const transport = [
  { type: "car", name: "Mietwagen - Hertz (BSP Auto)", vehicle: "GMC Yukon oder ahnlich, Full Size SUV, 7-Sitze", dates: "17.-29. Juli 2026", pickup: "Las Vegas Airport Harry Reid (LAS)", dropoff: "Pleasanton, Hopyard Rd & Stoneridge Dr, CA 94588", conf: "928654200445", detail: "Vollkasko ohne Selbstbeteiligung, unbegrenzte km" },
  { type: "rv", name: "Wohnmobil - El Monte RV / TUI Camper (ADAC)", vehicle: "El Monte RV Class C Family Sleeper (FS) 30-32ft", dates: "29. Juli - 7. Aug 2026", pickup: "San Francisco (EM), Abholung 13:00-13:30 Uhr", dropoff: "Seattle (EM/RB), Rückgabe 10:00-10:30 Uhr", conf: "VKB452414-1 (ADAC: C1001879/01)", detail: "Platinum Plus Paket, Drop & Go, Unlimited Miles, SLI-Versicherung, VIP Plus, 5× Bettwäsche, 5× Campingstühle, Kaffeemaschine, Toaster, GPS, Kitchen Kit, Generator unbegrenzt, Zusatzfahrer inkl. Gesamt: 4.604,14 EUR (ADAC) + 1.144,40 USD (El Monte). Tel: 888-337-2214 / +1-319-867-2059" },
  { type: "car", name: "Mietwagen - Seattle", vehicle: "Chevrolet Suburban oder ahnlich, Premium SUV (PFAV), Automatik", dates: "7.-11. Aug 2026", pickup: "Seattle-Tacoma Int. Flughafen (SEA), 3150 South 160th Street, 98188 SeaTac", dropoff: "Seattle-Tacoma Int. Flughafen (SEA)", conf: "9730760135", detail: "Abholung 14:00, Ruckgabe 10:30, 4 Gepackstucke" },
];

const itinerary = [
  { day: "1-3", dates: "17.-20. Juli", place: "Las Vegas", info: "Ankunft, Strip, Helikopterflug", color: "#E76F51", accom: "Bellagio Las Vegas", lat: 36.1699, lng: -115.1398 },
  { day: "2", dates: "18. Juli", place: "The Sphere – Wizard of Oz", info: "11:00 Uhr, Türen 10:15 Uhr", color: "#E76F51", activity: true, lat: 36.1825, lng: -115.1764 },
  { day: "3", dates: "19. Juli", place: "Grand Canyon Helikopterflug", info: "07:00 Uhr Abflug", color: "#E76F51", activity: true, lat: 36.0840, lng: -115.1537 },
  { day: "4-6", dates: "20.-23. Juli", place: "Los Angeles & Hollywood", info: "Hollywood, Santa Monica", color: "#E9C46A", accom: "Marina Escape Townhouse", lat: 34.0522, lng: -118.2437 },
  { day: "5", dates: "21. Juli", place: "Universal Studios VIP Tour", info: "Ganztagesausflug, 09:30 Uhr", color: "#E9C46A", activity: true, lat: 34.1381, lng: -118.3534 },
  { day: "7-8", dates: "23.-25. Juli", place: "Sequoia NP", info: "General Sherman Tree", color: "#2A9D8F", accom: "Wuksachi Lodge", lat: 36.4864, lng: -118.5658 },
  { day: "9-10", dates: "25.-27. Juli", place: "Yosemite NP", info: "El Capitan, Half Dome", color: "#2A9D8F", accom: "The Ahwahnee", lat: 37.8651, lng: -119.5383 },
  { day: "11-12", dates: "27.-29. Juli", place: "San Francisco", info: "Cable Car, Alcatraz, Wohnmobil-Übernahme", color: "#E9C46A", accom: "Argonaut Hotel", lat: 37.7749, lng: -122.4194 },
  { day: "12", dates: "28. Juli", place: "Alcatraz Island", info: "10:35 Uhr ab Pier 33, 10:05 da sein", color: "#E9C46A", activity: true, lat: 37.8267, lng: -122.4230 },
  { day: "13", dates: "29.-30. Juli", place: "Redwood NP - Süd", info: "Avenue of the Giants", color: "#2A9D8F", accom: "Benbow KOA Holiday, Garberville", lat: 40.0688, lng: -123.7893 },
  { day: "14-15", dates: "30. Jul-1. Aug", place: "Redwood NP - Nord", info: "Redwood Nationalpark", color: "#2A9D8F", accom: "Crescent City/Redwoods KOA", lat: 41.8028, lng: -124.1637 },
  { day: "16", dates: "1.-2. Aug", place: "Oregon Coast - Bullards Beach", info: "Bandon, Coquille River Lighthouse", color: "#264653", accom: "Bullards Beach State Park", lat: 43.1196, lng: -124.4087 },
  { day: "17", dates: "2.-3. Aug", place: "Oregon Coast - Waldport", info: "Newport, Oregon Coast Aquarium", color: "#264653", accom: "Waldport/Newport KOA Journey", lat: 44.4268, lng: -124.0695 },
  { day: "18", dates: "3.-4. Aug", place: "Oregon Coast - Astoria", info: "Astoria Column, Cannon Beach", color: "#264653", accom: "Astoria/Seaside KOA Resort", lat: 46.1785, lng: -123.9543 },
  { day: "19", dates: "4.-5. Aug", place: "La Push – Quileute", info: "Olympic NP Küste, Rialto Beach", color: "#2A9D8F", accom: "Quileute Oceanside Resort", lat: 47.9076, lng: -124.6367 },
  { day: "20-21", dates: "5.-7. Aug", place: "Olympic NP / Port Angeles", info: "Hoh Rainforest, Hurricane Ridge", color: "#2A9D8F", accom: "Port Angeles", lat: 48.1185, lng: -123.4308 },
  { day: "22", dates: "7.-8. Aug", place: "Seattle", info: "Wohnmobil-Rückgabe, Pike Place Market", color: "#E76F51", accom: "Sound Hotel", lat: 47.6062, lng: -122.3321 },
  { day: "23-24", dates: "8.-10. Aug", place: "Leavenworth", info: "Bayerisches Dorf in den Cascades", color: "#F4A261", accom: "Berg Haus Leavenworth", lat: 47.5962, lng: -120.6615 },
  { day: "25-26", dates: "10.-11. Aug", place: "Seattle", info: "Space Needle, Ende & Rückflug", color: "#E76F51", accom: "Hotel 1000", lat: 47.6062, lng: -122.3321 },
];

// ============ SORTABLE ITEM ============

const SortableStop = ({ item, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-2 items-start relative">
      <button {...attributes} {...listeners} className="mt-3 p-1 text-[#8D99AE] hover:text-[#264653] cursor-grab active:cursor-grabbing touch-none flex-shrink-0" data-testid={`drag-${item.id}`}>
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0 border-2 border-white shadow-sm mt-1" style={{ backgroundColor: item.color }}>
        {item.day}
      </div>
      <div className={`flex-1 rounded-xl border p-3 ${item.activity ? "bg-[#E9C46A]/10 border-[#E9C46A]/30" : "bg-white border-[#E0E0D0]"}`}>
        <div className="flex justify-between items-start">
          <div className="min-w-0 flex-1">
            <h3 className="font-nunito font-semibold text-sm text-[#264653]">
              {item.activity && <Ticket className="w-3.5 h-3.5 inline mr-1 text-[#E9C46A]" />}
              {item.place}
            </h3>
            <p className="font-nunito text-xs text-[#8D99AE] mt-0.5">{item.info}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            <span className="font-nunito text-[10px] text-[#8D99AE]">{item.dates}</span>
            <button onClick={() => onDelete(item.id)} className="p-1 text-[#8D99AE] hover:text-[#E76F51] transition-colors" data-testid={`delete-stop-${item.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {item.accom && (
          <div className="mt-2 pt-2 border-t border-[#E0E0D0]/60 flex items-start gap-1.5">
            <MapPin className="w-3 h-3 text-[#2A9D8F] mt-0.5 flex-shrink-0" />
            <span className="font-nunito text-[11px] text-[#2A9D8F] leading-tight">{item.accom}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ COMPONENT ============

const STORAGE_KEY = "dahrendorf_itinerary";
const DEFAULT_ITINERARY = itinerary.map((item, idx) => ({ ...item, id: `stop-${idx}` }));

const PlanPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plan");

  // Draggable itinerary state
  const [stops, setStops] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_ITINERARY;
    } catch { return DEFAULT_ITINERARY; }
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const saveStops = useCallback((newStops) => {
    setStops(newStops);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newStops));
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = stops.findIndex(s => s.id === active.id);
      const newIndex = stops.findIndex(s => s.id === over.id);
      saveStops(arrayMove(stops, oldIndex, newIndex));
      toast.success("Route aktualisiert");
    }
  }, [stops, saveStops]);

  const deleteStop = useCallback((id) => {
    saveStops(stops.filter(s => s.id !== id));
    toast.success("Stopp entfernt");
  }, [stops, saveStops]);

  const resetStops = useCallback(() => {
    saveStops(DEFAULT_ITINERARY);
    toast.success("Route zuruckgesetzt");
  }, [saveStops]);


  useEffect(() => {
    localApi.getTrips().then(t => {
      setTrips(t.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const trip = trips[0];

  const tabs = [
    { id: "plan", label: "Tagesplan", icon: CalendarDays },
    { id: "accom", label: "Unterkunfte", icon: Hotel },
    { id: "activities", label: "Aktivitaten", icon: Ticket },
    { id: "transport", label: "Fortbewegung", icon: Car },
    { id: "flights", label: "Fluge", icon: Plane },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="plan-page">
      {/* Header */}
      <div className="bg-[#264653] text-white px-4 py-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <Plane className="w-4 h-4 text-[#E9C46A]" />
            <span className="font-nunito text-xs text-white/70">USA Westkuste 2026</span>
          </div>
          <h1 className="font-fraunces text-2xl sm:text-3xl font-bold">Reiseplanung</h1>
          {trip && (
            <p className="font-nunito text-sm text-white/70 mt-1">
              {trip.start_date} bis {trip.end_date} &middot; 26 Tage
            </p>
          )}
        </div>
      </div>

      {/* Tabs - horizontal scroll on mobile */}
      <div className="bg-white border-b border-[#E0E0D0] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-2 flex gap-1.5 overflow-x-auto no-scrollbar">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-nunito font-semibold text-xs whitespace-nowrap transition-colors flex-shrink-0 ${
                activeTab === t.id
                  ? "bg-[#264653] text-white"
                  : "bg-[#F0EFEB] text-[#264653] hover:bg-[#E0E0D0]"
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-5">
        <div className="max-w-4xl mx-auto">

          {/* ========== TAGESPLAN ========== */}
          {activeTab === "plan" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-fraunces text-lg font-bold text-[#264653]">Tagesplan</h2>
                <div className="flex gap-2">
                  <button onClick={resetStops} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-nunito font-semibold text-[#8D99AE] hover:text-[#264653] bg-[#F0EFEB] rounded-lg transition-colors" data-testid="reset-stops">
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                </div>
              </div>
              <p className="font-nunito text-xs text-[#8D99AE] mb-3">Stopps per Drag & Drop verschieben oder loschen</p>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={stops.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {stops.map((item) => (
                      <SortableStop key={item.id} item={item} onDelete={deleteStop} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {stops.length === 0 && (
                <div className="text-center py-8">
                  <p className="font-nunito text-sm text-[#8D99AE]">Alle Stopps entfernt.</p>
                  <button onClick={resetStops} className="mt-2 text-[#2A9D8F] font-semibold text-sm">Route zurucksetzen</button>
                </div>
              )}

            </motion.div>
          )}

          {/* ========== UNTERKÜNFTE ========== */}
          {activeTab === "accom" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-fraunces text-lg font-bold text-[#264653] mb-4">Unterkunfte</h2>
              <div className="space-y-3">
                {accommodations.map((a, idx) => (
                  <motion.div key={idx}
                    className="bg-white rounded-xl border border-[#E0E0D0] p-4"
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                    data-testid={`accom-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${a.type === "camp" ? "bg-[#2A9D8F]/15" : "bg-[#264653]/10"}`}>
                        {a.type === "camp" ? <Tent className="w-4.5 h-4.5 text-[#2A9D8F]" /> : <Hotel className="w-4.5 h-4.5 text-[#264653]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <h3 className="font-nunito font-bold text-sm text-[#264653]">{a.name}</h3>
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${a.type === "camp" ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#264653]/10 text-[#264653]"}`}>
                            {a.type === "camp" ? "CAMPING" : "HOTEL"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <CalendarDays className="w-3 h-3 text-[#E9C46A]" />
                          <span className="font-nunito text-xs font-medium text-[#264653]">{a.dates}</span>
                          <span className="text-[10px] text-[#8D99AE]">({a.nights})</span>
                        </div>
                        <div className="flex items-start gap-1 mt-1">
                          <MapPin className="w-3 h-3 text-[#8D99AE] mt-0.5 flex-shrink-0" />
                          <span className="font-nunito text-[11px] text-[#8D99AE] leading-tight">{a.address}</span>
                        </div>
                        <p className="font-nunito text-[11px] text-[#264653] mt-1.5">{a.detail}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                          {a.conf && (
                            <span className="flex items-center gap-1 text-[10px] text-[#8D99AE]">
                              <Hash className="w-2.5 h-2.5" /> {a.conf}
                            </span>
                          )}
                          {a.phone && (
                            <span className="flex items-center gap-1 text-[10px] text-[#8D99AE]">
                              <Phone className="w-2.5 h-2.5" /> {a.phone}
                            </span>
                          )}
                          {a.email && (
                            <span className="flex items-center gap-1 text-[10px] text-[#8D99AE]">
                              <Mail className="w-2.5 h-2.5" /> {a.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ========== AKTIVITÄTEN ========== */}
          {activeTab === "activities" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-fraunces text-lg font-bold text-[#264653] mb-4">Aktivitaten</h2>
              <div className="space-y-3">
                {activities.map((a, idx) => (
                  <motion.div key={idx}
                    className="bg-white rounded-xl border border-[#E0E0D0] p-4"
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    data-testid={`activity-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#E9C46A]/15 flex items-center justify-center flex-shrink-0">
                        <Ticket className="w-4.5 h-4.5 text-[#E9C46A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-nunito font-bold text-sm text-[#264653]">{a.name}</h3>
                        <div className="flex items-center gap-1 mt-1">
                          <CalendarDays className="w-3 h-3 text-[#E9C46A]" />
                          <span className="font-nunito text-xs font-medium text-[#264653]">{a.date}</span>
                        </div>
                        <div className="flex items-start gap-1 mt-1">
                          <MapPin className="w-3 h-3 text-[#8D99AE] mt-0.5 flex-shrink-0" />
                          <span className="font-nunito text-[11px] text-[#8D99AE] leading-tight">{a.location}</span>
                        </div>
                        <p className="font-nunito text-[11px] text-[#264653] mt-1.5">{a.detail}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                          {a.conf && <span className="flex items-center gap-1 text-[10px] text-[#8D99AE]"><Hash className="w-2.5 h-2.5" /> {a.conf}</span>}
                          {a.phone && <span className="flex items-center gap-1 text-[10px] text-[#8D99AE]"><Phone className="w-2.5 h-2.5" /> {a.phone}</span>}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ========== FORTBEWEGUNG ========== */}
          {activeTab === "transport" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-fraunces text-lg font-bold text-[#264653] mb-4">Fortbewegung</h2>
              <div className="space-y-3">
                {transport.map((t, idx) => (
                  <motion.div key={idx}
                    className="bg-white rounded-xl border border-[#E0E0D0] p-4"
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}
                    data-testid={`transport-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${t.type === "rv" ? "bg-[#F4A261]/15" : "bg-[#264653]/10"}`}>
                        {t.type === "rv" ? <Caravan className="w-4.5 h-4.5 text-[#F4A261]" /> : <Car className="w-4.5 h-4.5 text-[#264653]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <h3 className="font-nunito font-bold text-sm text-[#264653]">{t.name}</h3>
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${t.type === "rv" ? "bg-[#F4A261]/15 text-[#F4A261]" : "bg-[#264653]/10 text-[#264653]"}`}>
                            {t.type === "rv" ? "WOHNMOBIL" : "MIETWAGEN"}
                          </span>
                        </div>
                        <p className="font-nunito text-xs text-[#8D99AE] mt-0.5">{t.vehicle}</p>
                        <div className="flex items-center gap-1 mt-1.5">
                          <CalendarDays className="w-3 h-3 text-[#E9C46A]" />
                          <span className="font-nunito text-xs font-medium text-[#264653]">{t.dates}</span>
                        </div>
                        <div className="mt-2 space-y-1.5 bg-[#F9F9F7] rounded-lg p-2.5">
                          <div className="flex items-start gap-1.5">
                            <span className="text-[10px] font-bold text-[#2A9D8F] mt-0.5">ANMIETUNG</span>
                            <span className="font-nunito text-[11px] text-[#264653]">{t.pickup}</span>
                          </div>
                          <div className="flex items-start gap-1.5">
                            <span className="text-[10px] font-bold text-[#E76F51] mt-0.5">ABGABE</span>
                            <span className="font-nunito text-[11px] text-[#264653] ml-[13px]">{t.dropoff}</span>
                          </div>
                        </div>
                        <p className="font-nunito text-[11px] text-[#264653] mt-1.5">{t.detail}</p>
                        {t.conf && (
                          <span className="flex items-center gap-1 text-[10px] text-[#8D99AE] mt-1.5">
                            <Hash className="w-2.5 h-2.5" /> {t.conf}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ========== FLÜGE ========== */}
          {activeTab === "flights" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-fraunces text-lg font-bold text-[#264653] mb-4">Fluge</h2>
              <div className="space-y-4">
                {flights.map((flight, idx) => (
                  <motion.div key={idx}
                    className="bg-white rounded-xl border border-[#E0E0D0] p-4"
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}
                    data-testid={`flight-${idx}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${flight.direction === "Hinflug" ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#E76F51]/15 text-[#E76F51]"}`}>
                        {flight.direction.toUpperCase()}
                      </span>
                      <span className="font-nunito text-xs font-medium text-[#264653]">{flight.date}</span>
                    </div>
                    <div className="space-y-3">
                      {flight.legs.map((leg, li) => (
                        <div key={li}>
                          {li > 0 && leg.transfer && (
                            <p className="text-[10px] text-[#2A9D8F] font-semibold mb-2 px-1">Transfer: {leg.transfer}</p>
                          )}
                          <div className="flex items-center gap-3 p-2.5 bg-[#F9F9F7] rounded-lg">
                            <div className="text-center flex-shrink-0">
                              <div className="font-nunito font-bold text-sm text-[#264653]">{leg.depTime}</div>
                              <div className="text-[10px] text-[#8D99AE]">{leg.from}</div>
                            </div>
                            <div className="flex-1 flex items-center gap-1">
                              <div className="h-px flex-1 bg-[#E0E0D0]" />
                              <Plane className="w-3 h-3 text-[#8D99AE]" />
                              <span className="text-[9px] text-[#8D99AE]">{leg.duration}</span>
                              <div className="h-px flex-1 bg-[#E0E0D0]" />
                            </div>
                            <div className="text-center flex-shrink-0">
                              <div className="font-nunito font-bold text-sm text-[#264653]">{leg.arrTime}</div>
                              <div className="text-[10px] text-[#8D99AE]">{leg.to}</div>
                            </div>
                          </div>
                          <p className="text-[10px] text-[#8D99AE] mt-1 px-1">{leg.airline} · {leg.aircraft}{leg.terminal ? ` · ${leg.terminal}` : ""}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

        </div>
      </div>

      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
    </div>
  );
};

export default PlanPage;
